import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import { ApiResponse } from '../../../types/api';

interface ChecklistGroupDetail {
  id: number;
  nome: string;
  descricao: string;
  tipo: string;
  quantidade_itens: number;
  itens_obrigatorios: number;
  status: string;
  ordem: number;
  categoria: string;
  empresa_id: number;
  criado_em: string;
  atualizado_em: string;
  itens?: Array<{
    id: number;
    descricao: string;
    categoria: string | null;
    obrigatorio: boolean;
    tipo: string;
    ordem: number;
    grupo_item_ordem?: number;
  }>;
}

const ChecklistGroupView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<ChecklistGroupDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadGroup = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.getChecklistGroup(Number(id)) as ApiResponse<any>;

      if (response.success && response.data) {
        const groupData = response.data;

        // Carregar itens vinculados ao grupo
        try {
          const itensResponse = await apiService.getChecklistGroupItems(Number(id));
          if (itensResponse.success && itensResponse.data) {
            groupData.itens = itensResponse.data;
          }
        } catch (itensError) {
          console.warn('Erro ao carregar itens do grupo:', itensError);
        }

        setGroup(groupData);
      } else {
        throw new Error(response.message || 'Erro ao carregar grupo');
      }
    } catch (error) {
      console.error('Erro ao carregar grupo:', error);
      setError('Erro ao carregar detalhes do grupo');
      utils.showNotification('Erro ao carregar detalhes do grupo', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Ativo': 'status-active',
      'Inativo': 'status-inactive'
    };
    return statusMap[status] || 'status-pending';
  };

  const getStatusText = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Ativo': 'Ativo',
      'Inativo': 'Inativo'
    };
    return statusMap[status] || status;
  };

  const getObrigatorioPercentage = (): number => {
    if (!group || group.quantidade_itens === 0) return 0;
    return Math.round((group.itens_obrigatorios / group.quantidade_itens) * 100);
  };

  const getObrigatorioClass = (percentage: number): string => {
    if (percentage >= 80) return 'status-success';
    if (percentage >= 50) return 'status-warning';
    return 'status-danger';
  };

  const getObrigatorioText = (percentage: number): string => {
    if (percentage >= 80) return 'Alta Taxa de Obrigatoriedade';
    if (percentage >= 50) return 'Taxa Moderada de Obrigatoriedade';
    return 'Baixa Taxa de Obrigatoriedade';
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/grupos-checklist')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Carregando Grupo...</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando detalhes do grupo...</p>
        </div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => navigate('/grupos-checklist')}>
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <h1>Erro ao Carregar</h1>
        </div>
        <div className="page-content">
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c', marginBottom: '20px' }}></i>
              <h3>{error || 'Grupo não encontrado'}</h3>
              <p>O grupo solicitado não pôde ser carregado.</p>
              <button className="btn btn-primary" onClick={() => navigate('/grupos-checklist')}>
                Voltar para Lista
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const obrigatorioPercentage = getObrigatorioPercentage();
  const obrigatorioClass = getObrigatorioClass(obrigatorioPercentage);
  const obrigatorioText = getObrigatorioText(obrigatorioPercentage);

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/grupos-checklist')}>
          <i className="fas fa-arrow-left"></i>
          Voltar
        </button>
        <h1>Detalhes do Grupo</h1>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/grupos-checklist/editar/${group.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Cabeçalho com informações principais */}
        <div className="card">
          <div className="card-header">
            <h2>{group.nome}</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span className={`status-badge ${getStatusClass(group.status)}`}>
                {getStatusText(group.status)}
              </span>
              <span className={`status-badge ${obrigatorioClass}`}>
                {obrigatorioPercentage}% Obrigatório
              </span>
              <span className="badge badge-secondary">
                Ordem: {group.ordem}
              </span>
            </div>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Descrição:</label>
                <span className="info-value">{group.descricao}</span>
              </div>
              <div className="info-item">
                <label>Tipo:</label>
                <span className="info-value">{group.tipo}</span>
              </div>
              <div className="info-item">
                <label>Categoria:</label>
                <span className="info-value">{group.categoria || '-'}</span>
              </div>
              <div className="info-item">
                <label>Quantidade de Itens:</label>
                <span className="info-value">{group.quantidade_itens}</span>
              </div>
              <div className="info-item">
                <label>Itens Obrigatórios:</label>
                <span className="info-value">
                  {group.itens_obrigatorios} ({obrigatorioPercentage}%)
                </span>
              </div>
              <div className="info-item">
                <label>Status Obrigatoriedade:</label>
                <span className="info-value">
                  <span className={`status-badge ${obrigatorioClass}`}>
                    {obrigatorioText}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Estatísticas do grupo */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Estatísticas do Grupo</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Total de Itens:</label>
                <span className="info-value">{group.quantidade_itens}</span>
              </div>
              <div className="info-item">
                <label>Itens Obrigatórios:</label>
                <span className="info-value">{group.itens_obrigatorios}</span>
              </div>
              <div className="info-item">
                <label>Itens Opcionais:</label>
                <span className="info-value">{group.quantidade_itens - group.itens_obrigatorios}</span>
              </div>
              <div className="info-item">
                <label>Taxa de Obrigatoriedade:</label>
                <span className="info-value">
                  <span className={`status-badge ${obrigatorioClass}`}>
                    {obrigatorioPercentage}%
                  </span>
                </span>
              </div>
              <div className="info-item">
                <label>Ordem de Execução:</label>
                <span className="info-value">{group.ordem}</span>
              </div>
              <div className="info-item">
                <label>Status do Grupo:</label>
                <span className="info-value">
                  <span className={`status-badge ${getStatusClass(group.status)}`}>
                    {getStatusText(group.status)}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de itens do grupo */}
        {/* Lista de itens do grupo */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Itens do Grupo ({group.itens ? group.itens.length : 0})</h3>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => navigate(`/grupos-checklist/editar/${group.id}`)}
              style={{ fontSize: '12px' }}
            >
              <i className="fas fa-plus" style={{ marginRight: '4px' }}></i>
              Gerenciar Itens
            </button>
          </div>
        {group.itens && group.itens.length > 0 ? (
            <div className="card-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {group.itens
                  .sort((a, b) => (a.grupo_item_ordem ?? a.ordem ?? 0) - (b.grupo_item_ordem ?? b.ordem ?? 0))
                  .map((item, index) => (
                    <div key={item.id} style={{ 
                      padding: '16px', 
                      border: '1px solid #e9ecef', 
                      borderRadius: '8px',
                      background: item.obrigatorio ? '#fff8e6' : '#f8f9fa'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <strong>{item.descricao}</strong>
                            {item.obrigatorio && (
                              <span className="status-badge status-danger" style={{ fontSize: '0.7rem' }}>
                                Obrigatório
                              </span>
                            )}
                            {!item.obrigatorio && (
                              <span className="status-badge status-info" style={{ fontSize: '0.7rem' }}>
                                Opcional
                              </span>
                            )}
                          </div>
                          {item.categoria && (
                            <div style={{ color: '#7f8c8d', fontSize: '0.9rem', marginBottom: '8px' }}>
                              {item.categoria}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <span className="badge badge-light">
                            Ordem: {index + 1}
                          </span>
                          <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>
                            {item.tipo}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
        ) : (
          <div className="card-body" style={{ textAlign: 'center', padding: '30px' }}>
            <i className="fas fa-inbox" style={{ fontSize: '2rem', color: '#adb5bd', marginBottom: '10px', display: 'block' }}></i>
            <p style={{ color: '#6c757d', margin: 0 }}>Nenhum item vinculado a este grupo.</p>
            <p style={{ color: '#adb5bd', fontSize: '13px', margin: '5px 0 0' }}>
              Clique em "Gerenciar Itens" para vincular itens de checklist.
            </p>
          </div>
        )}
        </div>

        {/* Informações de auditoria */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Informações de Auditoria</h3>
          </div>
          <div className="card-body">
            <div className="info-grid">
              <div className="info-item">
                <label>Criado em:</label>
                <span className="info-value">{formatDateTime(group.criado_em)}</span>
              </div>
              <div className="info-item">
                <label>Atualizado em:</label>
                <span className="info-value">{formatDateTime(group.atualizado_em)}</span>
              </div>
              <div className="info-item">
                <label>ID do Grupo:</label>
                <span className="info-value">{group.id}</span>
              </div>
              <div className="info-item">
                <label>ID da Empresa:</label>
                <span className="info-value">{group.empresa_id}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/grupos-checklist')}
          >
            <i className="fas fa-list"></i>
            Voltar para Lista
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => navigate(`/grupos-checklist/editar/${group.id}`)}
          >
            <i className="fas fa-edit"></i>
            Editar Grupo
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChecklistGroupView;
